import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import useAdminLoad from "@/hooks/useAdminLoad";
import { useAdmin } from "@/admin/AuthContext";
import { Empty, LoadState, PageHeader, Panel, Pill } from "@/components/admin/AdminUI";
import ConfirmDialog from "@/components/admin/ConfirmDialog";
import CreateAdminDialog, { TempPasswordDialog } from "@/components/admin/CreateAdminDialog";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/format";

function Row({ a, me, onAction }) {
  const locked = a.role === "owner";
  return (
    <TableRow data-testid={`admin-row-${a.id}`}>
      <TableCell className="pl-5"><p className="font-medium" data-no-i18n>{a.name || a.email}</p><p className="cw-muted text-xs">{a.email}</p></TableCell>
      <TableCell>{locked ? <Pill tone="brand" className="capitalize">{a.role}</Pill> : (
        <Select value={a.role} onValueChange={(role) => role !== a.role && onAction(a, "role", role)}>
          <SelectTrigger className="h-9 w-36 capitalize" aria-label={`Role for ${a.email}`} data-testid={`admin-role-select-${a.id}`}><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="moderator" data-testid={`admin-role-option-moderator-${a.id}`}>Moderator</SelectItem><SelectItem value="support" data-testid={`admin-role-option-support-${a.id}`}>Support</SelectItem></SelectContent>
        </Select>)}</TableCell>
      <TableCell><Pill tone={a.active ? "success" : "neutral"} data-testid={`admin-status-${a.id}`}>{a.active ? "Active" : "Deactivated"}</Pill></TableCell>
      <TableCell className="cw-muted whitespace-nowrap">{formatDate(a.last_login_at)}</TableCell>
      <TableCell className="pr-5 text-right">
        {locked ? <span className="cw-muted text-xs">{a.id === me ? "You" : "Owner"}</span> : (
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => onAction(a, "reset")} data-testid={`admin-reset-password-button-${a.id}`}>Reset password</Button>
            {a.active
              ? <Button size="sm" variant="outline" className="cw-error" onClick={() => onAction(a, "deactivate")} data-testid={`admin-deactivate-button-${a.id}`}>Deactivate</Button>
              : <Button size="sm" variant="outline" onClick={() => onAction(a, "reactivate")} data-testid={`admin-reactivate-button-${a.id}`}>Reactivate</Button>}
          </div>
        )}
      </TableCell>
    </TableRow>
  );
}

const COPY = {
  deactivate: { title: "Deactivate", desc: "They're signed out and can't sign in until reactivated.", label: "Deactivate", destructive: true },
  reactivate: { title: "Reactivate", desc: "They can sign in again with their current password.", label: "Reactivate", destructive: false },
  role: { title: "Change the role of", desc: "Their access changes on their next request.", label: "Change role", destructive: false },
  reset: { title: "Reset password for", desc: "Their current password stops working. You'll see a new temporary password once.", label: "Reset password", destructive: true },
};

export default function Admins() {
  const { admin: me } = useAdmin();
  const { data, error, loading, reload } = useAdminLoad("/admin/admins");
  const [pending, setPending] = useState(null);
  const [temp, setTemp] = useState(null);

  const run = async () => {
    const { a, action } = pending;
    if (action === "reset") {
      const res = await api(`/admin/admins/${a.id}/reset-password`, { method: "POST" });
      setTemp({ email: a.email, password: res.temporary_password });
    } else if (action === "role") {
      await api(`/admin/admins/${a.id}`, { method: "PATCH", body: { role: pending.role } });
      toast.success(`${a.email} is now ${pending.role}.`);
    } else {
      await api(`/admin/admins/${a.id}`, { method: "PATCH", body: { active: action === "reactivate" } });
      toast.success(action === "reactivate" ? "Admin reactivated." : "Admin deactivated.");
    }
    reload();
  };
  const c = pending && COPY[pending.action];

  return (
    <div data-testid="admin-team-page">
      <PageHeader title="Team" description="Moderators handle Verification, Reports, Appeals and user moderation. Support helps people: user lookup, exports, short tier grants, notes and the waitlist."
        actions={<CreateAdminDialog onCreated={(res) => { reload(); if (res.temporary_password) setTemp({ email: res.admin.email, password: res.temporary_password }); else toast.success(res.email_test_logged ? "Invite logged (test email mode), not sent." : res.emailed ? `Invite emailed to ${res.admin.email}.` : "Admin created."); }} />} />
      <LoadState loading={loading} error={error} onRetry={reload}>
        <Panel className="overflow-hidden">
          {data?.items?.length ? (
            <Table data-testid="admins-table">
              <TableHeader><TableRow><TableHead className="pl-5">Admin</TableHead><TableHead>Role</TableHead><TableHead>Status</TableHead><TableHead>Last sign-in</TableHead><TableHead className="pr-5 text-right">Actions</TableHead></TableRow></TableHeader>
              <TableBody>{data.items.map((a) => <Row key={a.id} a={a} me={me?.id} onAction={(aa, action, role) => setPending({ a: aa, action, role })} />)}</TableBody>
            </Table>
          ) : <Empty title="No admins yet" />}
        </Panel>
      </LoadState>
      <ConfirmDialog open={!!pending} onOpenChange={(o) => !o && setPending(null)} testId="admin-action-confirm-dialog"
        title={c ? `${c.title} ${pending.a.email}${pending.action === "role" ? ` to ${pending.role}` : ""}?` : ""} description={c?.desc} confirmLabel={c?.label} destructive={c?.destructive} onConfirm={run} />
      <TempPasswordDialog value={temp} onClose={() => setTemp(null)} />
    </div>
  );
}
